import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RedirectCommand, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { RouteService } from '@shared/core/route.service';
import { __ } from '@shared/functions/object.functions';
import { RedirectService } from '@shared/services/local/redirect.service';
import { ToastService } from '@shared/services/local/toast.service';
import { OAuthService, OAuthSuccessEvent } from 'angular-oauth2-oidc';
import { Observable, of, switchMap } from 'rxjs';
import { ROOT_URLS } from '../constants/urls.constants';
import { AuthenticationService } from './authentication.service';

@Injectable({ providedIn: 'root' })
export class AuthenticationGuard implements CanActivate {

  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor(
    private router: Router,
    private oAuthService: OAuthService,
    private authenticationService: AuthenticationService,
    private redirectService: RedirectService,
    private toast: ToastService,
    private translateService: TranslateService
  ) { }

  // -----------------------------------------------------------------------------------------------------
  // @ INTERFACES
  // -----------------------------------------------------------------------------------------------------

  /**
   * Whether the user is allowed to access the route.
   * 
   * @param {ActivatedRouteSnapshot} route - The route to check.
   * @returns {Observable<boolean>} - Whether the user is allowed to access the route.
   */
  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | RedirectCommand> {
    const isLoginCallback = location.href.includes('code=');

    // Set the redirect url if the access token is invalid, since it will then
    // be refreshed
    if (this.oAuthService.hasValidAccessToken() === false) {
      this.redirectService.setRedirectUri(window.location.pathname);
    }

    return this.authenticationService
      .checkLoginStatusOrLogin()
      .pipe(
        switchMap((shouldDisplayApplication: boolean | OAuthSuccessEvent) => {
          // Disallow access when we are in a login flow
          if (shouldDisplayApplication === false) {
            return of(false);
          }

          // Check privileges
          const data = RouteService.MergeData(route);

          // If there are roles present in the route configuration, and the currently logged in user
          // does not fit the role, check, if we should route to the forbidden page
          if (!__.IsNullOrUndefined(data['roles']) && !this.authenticationService.hasAnyRole(data['roles'])) {
            return of(this.checkForbidden(route));
          }

          // If a redirect uri is present and we are in the login flow, redirect to the specific page,
          // since the route state was lost during external login redirection
          if (!__.IsNullOrUndefinedOrEmpty(this.redirectService.redirectUri()) && isLoginCallback) {
            const redirectUri: string | undefined = this.redirectService.redirectUri();

            if (this.redirectService.redirectUri() !== '/') {
              this.redirectService.reset();
            }

            return of(new RedirectCommand(this.router.parseUrl(redirectUri!)));
          }

          // All good, the user is allowed to pass
          return of(true);
        })
      );
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Checks if the user is forbidden to access the route.
   * 
   * @param {ActivatedRouteSnapshot} route - The route to check.
   * @returns {boolean} - Whether the user is forbidden to access the route.
   */
  private checkForbidden(route: ActivatedRouteSnapshot): boolean {
    // TODO: POTENTIALLY BREAKING
    if ((route as any)['_routerState'].url === '/' || (route as any)['_routerState'].url.includes(ROOT_URLS.dashboard)) {
      return true;
    }

    this.toast.add(
      this.translateService.instant('General.You are not allowed to view this page. Please ask your administrator for permissions to view this page'),
      {
        status: 'error',
        duration: 6000
      });

    this.router.navigate([ROOT_URLS.dashboard], { replaceUrl: true });

    return false;
  }
}
